import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Clock, MapPin, Car, CalendarCheck, CheckCircle2, AlertTriangle } from 'lucide-react';
import { API } from '@/config/api';

const today = () => new Date().toISOString().split('T')[0];

const prettyDate = (value) =>
  new Date(`${value}T00:00:00`).toLocaleDateString('en-ZA', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
  });

const shift = (value, days) => {
  const date = new Date(`${value}T00:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
};

/** A rep's day at a glance: timed appointments, flexible visits and the drive between stops. */
export const DayPlanner = () => {
  const [date, setDate] = useState(today());
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (day) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/rep/day-plan`, { params: { date: day } });
      setPlan(res.data);
    } catch {
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(date);
  }, [date, load]);

  const appointments = plan?.appointments || [];
  const flexible = plan?.flexible_appointments || [];
  const logged = plan?.logged || [];
  const gaps = plan?.travel_gaps || [];
  const tight = gaps.filter((gap) => gap.tight);

  return (
    <Card data-testid="day-planner">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarCheck className="w-4 h-4" />
            My day — {prettyDate(date)}
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 px-2 text-xs"
              onClick={() => setDate(shift(date, -1))}
              data-testid="day-planner-prev"
            >
              Prev
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 px-2 text-xs"
              onClick={() => setDate(today())}
              data-testid="day-planner-today"
            >
              Today
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 px-2 text-xs"
              onClick={() => setDate(shift(date, 1))}
              data-testid="day-planner-next"
            >
              Next
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && <p className="text-xs text-muted-foreground">Loading your day…</p>}

        {!loading && tight.length > 0 && (
          <div
            className="flex gap-2 rounded-md border border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/40 px-3 py-2"
            data-testid="day-planner-tight"
          >
            <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
            <div className="text-xs">
              <p className="font-semibold">Tight travel today</p>
              {tight.map((gap, index) => (
                <p key={index} className="mt-0.5">
                  {gap.from_area} → {gap.to_area}: {gap.distance_km}km needs about{' '}
                  {gap.travel_minutes} mins, you have {gap.gap_minutes} mins.
                </p>
              ))}
            </div>
          </div>
        )}

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            Appointments ({appointments.length})
          </p>
          {appointments.length === 0 && !loading && (
            <p className="text-xs text-muted-foreground">No timed appointments booked.</p>
          )}
          <div className="space-y-2">
            {appointments.map((item, index) => (
              <div key={item.appointment_id}>
                <div
                  className="flex items-start gap-3 rounded-md border bg-blue-50/60 dark:bg-blue-950/30 px-3 py-2"
                  title={`${item.visit_type || 'Appointment'} with ${item.doctor_name} — ${item.area || 'area unknown'}`}
                  data-testid={`day-appointment-${item.appointment_id}`}
                >
                  <span className="flex items-center gap-1 text-xs font-bold tabular-nums shrink-0">
                    <Clock className="w-3 h-3" />
                    {item.appointment_time}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{item.doctor_name}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 flex-wrap">
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                        {item.visit_type || 'Appointment'}
                      </Badge>
                      <span className="flex items-center gap-0.5">
                        <MapPin className="w-3 h-3" />
                        {item.area || 'Area unknown'}
                      </span>
                      {item.practice_name && <span className="truncate">· {item.practice_name}</span>}
                    </p>
                  </div>
                </div>
                {gaps[index] && (
                  <p
                    className={`text-[11px] mt-1 ml-3 flex items-center gap-1 ${
                      gaps[index].tight ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'
                    }`}
                    data-testid={`day-gap-${index}`}
                  >
                    <Car className="w-3 h-3" />
                    {gaps[index].distance_km != null
                      ? `${gaps[index].distance_km}km · about ${gaps[index].travel_minutes} mins drive`
                      : 'Drive time unknown'}
                    {` · ${gaps[index].gap_minutes} mins between`}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            Planned visits — no fixed time ({flexible.length})
          </p>
          {flexible.length === 0 && !loading && (
            <p className="text-xs text-muted-foreground">Nothing flexible planned.</p>
          )}
          <div className="space-y-2">
            {flexible.map((item) => (
              <div
                key={item.appointment_id}
                className="flex items-start gap-2 rounded-md border border-dashed px-3 py-2"
                title={`${item.visit_type || 'Visit'} with ${item.doctor_name} — ${item.area || 'area unknown'}`}
                data-testid={`day-visit-${item.appointment_id}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{item.doctor_name}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1 flex-wrap">
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                      {item.visit_type || 'Visit'}
                    </Badge>
                    <span className="flex items-center gap-0.5">
                      <MapPin className="w-3 h-3" />
                      {item.area || 'Area unknown'}
                    </span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {logged.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Already logged ({logged.length})
            </p>
            <div className="space-y-1.5">
              {logged.map((item) => (
                <p
                  key={item.visit_id}
                  className="text-xs flex items-center gap-1.5 flex-wrap"
                  title={`${item.visit_type || 'Visit'} — ${item.area || 'area unknown'}`}
                  data-testid={`day-logged-${item.visit_id}`}
                >
                  <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
                  <span className="font-medium">{item.doctor_name}</span>
                  <span className="text-muted-foreground">{item.visit_type}</span>
                  <span className="text-muted-foreground flex items-center gap-0.5">
                    <MapPin className="w-3 h-3" />
                    {item.area || 'Area unknown'}
                  </span>
                </p>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DayPlanner;
